import {
  ClientBookingDataExtra,
  ClientBookingDetailExtra,
  ClientBookingDataTransferType,
  ClientBookingDetail,
  ClientBookingQuote,
} from "@/redux/apis/clientBooking/types";
import { mapBookingDetailToRebookSource, buildRebookDraft } from "./rebookDraft";
import {
  BookingPassenger,
  BookingPassengerDetail,
  ClientBookingExtra,
  ClientBookingState,
  PassengerContact,
  SELF_PASSENGER_ID,
} from "./types";

/**
 * The backend returns `pickup_time` as an ISO carrying the PICKUP location's timezone offset
 * (e.g. "2026-08-01T09:00:00+10:00"). The booking flow's `pickupDatetime` follows a "naive wall clock"
 * convention (see `flightPickup.ts`): its value, rendered BROWSER-local by the payload's
 * `format(new Date(x), "yyyy-MM-dd HH:mm:ss")`, must reproduce the intended wall clock. Storing the raw
 * offset ISO breaks that — re-submitting an edit (even one that never touched the time) re-encodes the
 * pickup in the BROWSER's timezone, shifting the stored instant by the browser↔pickup gap and re-sorting
 * the booking. So convert to the pickup's own wall clock re-encoded as a browser-local Date, matching how
 * the manual picker and flight paths commit their values, so the round-trip is exact.
 */
export const toNaiveWallClockIso = (iso: string): string => {
  const match = iso.match(/([+-])(\d{2}):?(\d{2})$/);
  if (!match) return iso; // no offset — already a naive/local value; leave as-is
  const offsetMs = (match[1] === "-" ? -1 : 1) * (parseInt(match[2], 10) * 60 + parseInt(match[3], 10)) * 60_000;
  // Shift so the Date's UTC fields hold the pickup's wall clock, then re-read those fields as browser-local.
  const wall = new Date(Date.parse(iso) + offsetMs);
  return new Date(
    wall.getUTCFullYear(),
    wall.getUTCMonth(),
    wall.getUTCDate(),
    wall.getUTCHours(),
    wall.getUTCMinutes(),
    0,
    0
  ).toISOString();
};

/**
 * Snapshot the finished booking's stored price into a quote-shaped object so the view-only Price
 * Summary renders it verbatim (journey cost, extras, fees, total) without a live re-quote. Returns
 * null when pricing isn't available for this client (`total` omitted) — the summary then shows no price.
 */
const buildStoredQuote = (detail: ClientBookingDetail): ClientBookingQuote | null => {
  // The operator can hide pricing from the client (is_pricing_available=false) — the backend then
  // strips total AND every itemised cost, so there's no meaningful breakdown to show. Respect that
  // rather than rendering a zero breakdown (or a lone leaked vehicle.total).
  if (detail.flags?.is_pricing_available === false) return null;
  // total is present when pricing is enabled; fall back to the other priced fields just in case.
  const total = detail.total ?? detail.estimated_payment ?? detail.vehicle?.total;
  if (total == null) return null;
  return {
    quote_id: String(detail.job_id),
    account_uuid: "",
    vehicles: {
      cost: detail.vehicle?.cost ?? detail.vehicle?.total ?? 0,
      type: detail.vehicle?.type ?? 0,
      "type-friendly": detail.vehicle?.type_friendly ?? "",
      components: (detail.extras ?? []).map((extra) => ({ name: extra.name, cost: extra.cost ?? 0, type: "extra" })),
    },
    country_rule: "",
    city_rule: "",
    total: String(total),
    processing_fees: detail.processing_fees?.length
      ? { components: detail.processing_fees.map((fee) => ({ name: fee.name, cost: fee.cost ?? 0 })) }
      : undefined,
  };
};

/**
 * A priced invoice line item names its quantity — "Baby Seat (2)" — where the reference data holds
 * the bare option label. Stripped so the two can be compared.
 */
const stripQuantitySuffix = (name: string) => name.replace(/\s*\(\d+\)\s*$/, "").trim();

/**
 * ...and read, rather than only stripped. That suffix is the ONLY record of how many were booked:
 * the detail returns one row per extra however many were bought, and the draft used to rebuild each
 * one from reference data, which knows nothing about this booking. So two baby capsules hydrated as
 * one, the sheet showed no quantity, and any edit that triggered a re-quote silently re-priced the
 * booking at one — the same class of bug as an extra that fails to resolve at all (see
 * matchStoredExtra). Absent suffix means one.
 */
const parseQuantitySuffix = (name: string): number => {
  const match = /\((\d+)\)\s*$/.exec(name);
  const quantity = match ? Number(match[1]) : 1;
  return quantity > 0 ? quantity : 1;
};

/**
 * Resolve a stored extra back to the operator's reference-data entry.
 *
 * Getting this wrong is expensive and silent. The draft renders `name`, but the quote payload sends
 * `extra_id` — so an unresolved extra still SHOWS in Trip Details while vanishing from the price. A
 * $25 baby seat booked at $100.87 re-quoted at $74.96 on the edit screen, the missing $25.91 being
 * the extra plus its share of the processing fee.
 *
 * Matched three ways because the detail's extras arrive from two different places. Free extras come
 * from getJobExtraOptionsById with the clean option_label and no `line-item`; PAID ones come from the
 * invoice components, whose name carries the quantity suffix. Only free extras matched on name alone,
 * which is why this went unnoticed.
 */
export const matchStoredExtra = (extra: ClientBookingDetailExtra, options: ClientBookingDataExtra[]) => {
  const lineItem = extra["line-item"];
  if (lineItem) {
    const byId = options.find((option) => String(option.extra_id) === String(lineItem));
    if (byId) return byId;
  }

  return (
    options.find((option) => option.name === extra.name) ??
    options.find((option) => stripQuantitySuffix(option.name) === stripQuantitySuffix(extra.name))
  );
};

/**
 * Build a FULL booking draft from a finished trip, for the read-only "view booking" sheet (DEV-446).
 *
 * Unlike a re-book (`buildRebookDraft`, which deliberately clears the date, flight number, extras and
 * payment so the next trip starts fresh), view-only must replay the booking EXACTLY as it was — so we
 * start from the re-book's route/vehicle/transfer/counts and then restore everything it dropped:
 * pickup date/time, flight number, chosen extras, the passenger the trip was for, and the Other
 * Details (booked-by / reference / additional passengers). Payment is resolved separately in the sheet
 * (the detail carries only the card type + uuid; the full saved card comes from the payment-methods
 * list, matched by `paymentMethodUuid`).
 */
export const mapBookingDetailToViewDraft = (
  detail: ClientBookingDetail,
  transferTypes: ClientBookingDataTransferType[] = [],
  extras: ClientBookingDataExtra[] = []
): Partial<ClientBookingState> => {
  const base = buildRebookDraft(mapBookingDetailToRebookSource(detail, transferTypes), "duplicate");

  // The booking's OWN transfer type when the client job-data no longer lists it — deactivated, deleted
  // (soft: the row and its name survive) or admin-only. A replay honours the type the booking was made
  // on: it shows under Vehicle and Transfer, and goes back unchanged as `listed_events_list` unless the
  // client chooses another — the PUT accepts it, BookingResource::process() checks existence only.
  // Forcing a reselect here would reprice a booking the client only came to retime (J389087, DEV-1636).
  // Needs the id AND a name: a backend sending only the label leaves it null, as before, and the sheet
  // asks for a transfer type. `type` is the job's own rate, the same value the type carried when booked.
  const transferType =
    base.transferType ??
    (detail.transfer_type_id != null && detail.transfer_type?.trim()
      ? { id: detail.transfer_type_id, label: detail.transfer_type.trim(), type: detail.journey_type }
      : null);

  // Only actual add-ons belong in the Extras field — the detail's `extras` also carries fees (levy,
  // processing) which are priced but aren't bookable extras. Resolve each to its reference-data id (so
  // the control shows it selected); fall back to the stored line-item / name when no longer offered.
  const selectedExtras: ClientBookingExtra[] = (detail.extras ?? [])
    .filter((extra) => (extra.type ?? "").toLowerCase() === "extra")
    .map((extra) => {
      const match = matchStoredExtra(extra, extras);
      const quantity = parseQuantitySuffix(extra.name);
      // The unmatched fallback keeps the detail's own name, so strip the suffix there too — the
      // quantity is carried by the field now, and the label would otherwise read "Baby Seat (2) (2)".
      return match
        ? { extra_id: match.extra_id, name: match.name, amount: match.amount, quantity }
        : { extra_id: extra["line-item"] ?? extra.name, name: stripQuantitySuffix(extra.name), quantity };
    });

  // The trip's main passenger. `is_guest` ⇒ a chosen guest (kind "guest"); otherwise the account
  // holder (null ⇒ "self", the sheet's default), matching how the passenger step stores it.
  const p = detail.passenger;
  const passenger: BookingPassenger | null =
    p?.is_guest && (p.first_name || p.last_name)
      ? {
          id: "view-only-passenger",
          title: p.title,
          firstName: p.first_name ?? "",
          lastName: p.last_name ?? "",
          phone: p.phone ?? "",
          email: p.email ?? "",
          kind: "guest",
          favouriteId: p.favourite_passenger_id ?? null,
        }
      : null;

  const passengerContactOverrides: Record<string, PassengerContact> =
    !passenger && p && (p.first_name || p.last_name)
      ? {
          [SELF_PASSENGER_ID]: {
            ...(p.title ? { title: p.title } : {}),
            firstName: p.first_name ?? "",
            lastName: p.last_name ?? "",
            phone: p.phone ?? "",
            email: p.email ?? "",
          },
        }
      : {};

  // Other Details "Passenger Details": row 0 is the main passenger, rows 1..N the additional ones.
  const mainName = [p?.first_name, p?.last_name].filter(Boolean).join(" ").trim();
  const passengers: BookingPassengerDetail[] = [
    { name: mainName, phone: p?.phone ?? "" },
    ...(detail.passengers ?? []).map((extra) => ({ name: extra.name, phone: extra.phone ?? "" })),
  ];

  return {
    ...base,
    transferType,
    viewOnly: true,
    // Undefined against a backend that predates the field — the sheet then infers from coordinates.
    isJobVerified: detail.flags?.is_verified,
    pickupDatetime: detail.pickup_time ? toNaiveWallClockIso(detail.pickup_time) : null,
    pickupFlightNumber: detail.flight_number || null,
    pickupNotFlying: detail.flight_not_flying === true,
    // Without this the Date/Time panel recomputed `pickup = landing + 0` on Confirm and silently
    // clobbered the delayed time the client booked. `?? null` keeps a stored 0 as 0 (DEV-1429).
    pickupFlightDelayMinutes: detail.flight_pickup_delay_minutes ?? null,
    // Deliberately NOT hydrated from the stored arrival. This field is the operand of a pickup this
    // session actually derived, and an untouched edit re-commits the booked pickup — which the server
    // skips provenance for entirely (the pickup did not move). Re-deriving writes the fresh landing here.
    pickupFlightLandingUsed: null,
    // The arrival the stored pickup was built from, so the panel can show a working that reconciles
    // rather than pairing the booked pickup with wherever the flight has since moved to.
    pickupFlightDerivedArrival: detail.flight_pickup_derived_arrival ?? null,
    pickupFlightDerivedArrivalSource: detail.flight_pickup_derived_arrival_source ?? null,
    pickupFlightFrozenAt: detail.flight_pickup_frozen_at ?? null,
    // Stored, so it needs no flight lookup — which is what lets a closed booking still disclose a
    // cancellation. 127 of 175 frozen bookings on staging are closed.
    pickupFlightFrozenReason: detail.flight_pickup_frozen_reason ?? null,
    // The DEPARTURE flight as booked (DEV-1487), so an edit can show it, change it, or clear it. Only
    // the NUMBER hydrates: `departureTerminalSource` explains a swap that happened while the client was
    // editing, so a replayed booking has nothing to disclose — same rule as `pickupTerminalSource`.
    departureFlightNumber: detail.departure_flight_number || null,
    // The booked time is whatever the server holds, however it was arrived at. Marking it "manual" is
    // what stops a lookup on open silently re-applying a SUGGESTED PICKUP over a time the client
    // already committed — only a departure-flight CHANGE reopens the suggestion, via the slice.
    pickupTimeSource: detail.departure_flight_number ? "manual" : null,
    extras: selectedExtras,
    passenger,
    // A chosen guest must also live in sessionGuests so the passenger panel's row list (which is
    // [self, ...favourites, ...sessionGuests]) actually contains the selected passenger to render.
    sessionGuests: passenger ? [passenger] : [],
    passengerContactOverrides,
    passengers,
    bookedBy: detail.booked_by,
    reference: detail.reference,
    storedQuote: buildStoredQuote(detail),
  };
};
